import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { CloseX } from '@elliemae/ds-icons';
import { describe } from '@elliemae/ds-props-helpers';
import { icons } from './utils/icons.js';
import type { DSMobileBannerT } from './propTypes.js';
import { propTypes } from './propTypes.js';
import {
  StyledActionLink,
  StyledCloseButton,
  StyledInnerContainer,
  StyledBannerContainer,
  StyledContent,
  StyledIconContainer,
  StyledSubTitle,
  StyledTitle,
  StyledSubTitleText,
  StyledCloseButtonContainer,
} from './styles.js';
import { useDSMobileBanner } from './config/useMobileBanner.js';
import { DSMobileBannerName } from './DSMobileBannerDefinitions.js';

const DSMobileBanner = (props: DSMobileBannerT.Props) => {
  const { globalAttributes, xstyledProps, propsWithDefault } = useDSMobileBanner(props);
  const {
    type,
    isOpen,
    closeButtonOnClick,
    label,
    body,
    actionLinkLabel,
    actionLinkHref,
    actionLinkOnClick,
    showCloseButton,
    containerProps,
    focusOnOpen,
  } = propsWithDefault;

  const innerContainerRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLDivElement | null>(null);
  const [current, setCurrent] = useState<boolean>(isOpen);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (innerContainerRef.current) {
      setHeight(innerContainerRef.current.offsetHeight);
    }
  }, [isAnimating, isOpen]);

  useEffect(() => {
    if (focusOnOpen && closeRef.current && isOpen) {
      closeRef.current?.focus?.();
    }
  }, [focusOnOpen, isOpen]);

  useLayoutEffect(() => {
    if (isOpen !== current || isOpen) setIsAnimating(true);
    else setIsAnimating(false);
  }, [isOpen, current]);

  if (!isAnimating && !isOpen) return null;

  return (
    <StyledBannerContainer
      type={type}
      isOpen={isOpen}
      isAnimating={isAnimating}
      onAnimationEnd={() => setCurrent(isOpen)}
      height={height}
      {...containerProps}
      data-testid="ds-mobile-banner-container"
      role="alert"
      {...globalAttributes}
      {...xstyledProps}
    >
      <StyledInnerContainer type={type} isOpen={isOpen} innerRef={innerContainerRef} isAnimating={isAnimating}>
        <StyledIconContainer data-testid="ds-mobile-banner-icon">{icons[type]}</StyledIconContainer>
        <StyledContent showCloseButton={showCloseButton}>
          <StyledTitle>{label}</StyledTitle>
          <StyledSubTitle>
            <StyledSubTitleText>{body}</StyledSubTitleText>
            {actionLinkLabel && (
              <StyledActionLink href={actionLinkHref} data-testid="ds-mobile-banner-link" onClick={actionLinkOnClick}>
                {actionLinkLabel}
              </StyledActionLink>
            )}
          </StyledSubTitle>
        </StyledContent>
        {showCloseButton && (
          <StyledCloseButtonContainer>
            <StyledCloseButton
              data-testid="ds-mobile-banner-close-button"
              buttonType="icon"
              onClick={closeButtonOnClick}
              aria-label="Close Banner"
              innerRef={closeRef}
              size="s"
            >
              <CloseX width="12px" height="12px" />
            </StyledCloseButton>
          </StyledCloseButtonContainer>
        )}
      </StyledInnerContainer>
    </StyledBannerContainer>
  );
};

DSMobileBanner.displayName = DSMobileBannerName;
const DSMobileBannerWithSchema = describe(DSMobileBanner);
DSMobileBannerWithSchema.propTypes = propTypes;

export { DSMobileBanner, DSMobileBannerWithSchema };
